// BUILD-042 - Activity Log: pure presentation/state helpers (no React, no fetching).
// Kept separate from AuditPanel.tsx so the exact logic the screen runs can be tested under plain
// Node (see activityLogModel.test.mjs), the same pattern editTemplateSave.ts uses.
//
// Nothing here makes an authorization decision. The server already applied the caller's scope
// before any filter; this file only turns what the person typed into a query and turns the rows the
// server returned into readable text.

import type { HrAuditQuery, HrAuditEventRow } from '../../api/client';
import { formatLocalDate } from './hrFormat';

export interface ActivityFilters {
  employee: string;
  actor: string;
  keyword: string;
  action: string;
  /** Native date input values: YYYY-MM-DD (America/Phoenix calendar days, inclusive). */
  from: string;
  to: string;
}

export const EMPTY_FILTERS: ActivityFilters = { employee: '', actor: '', keyword: '', action: '', from: '', to: '' };
export const PAGE_SIZES = [25, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 25;

export function hasActiveFilters(f: ActivityFilters): boolean {
  return (Object.keys(EMPTY_FILTERS) as Array<keyof ActivityFilters>).some((k) => f[k].trim() !== '');
}

/** Normalize what is typed (trim; collapse inner whitespace) so the same search always looks the same. */
export function normalizeFilters(f: ActivityFilters): ActivityFilters {
  const t = (s: string) => s.replace(/\s+/g, ' ').trim();
  return { employee: t(f.employee), actor: t(f.actor), keyword: t(f.keyword), action: f.action.trim(), from: f.from.trim(), to: f.to.trim() };
}

/** null when the filters are fine, otherwise a plain-language problem to show before any request. */
export function validateFilters(f: ActivityFilters): string | null {
  if (f.from && f.to && f.from > f.to) return 'The start date must be on or before the end date.';
  return null;
}

export function buildAuditQuery(f: ActivityFilters, limit: number, cursor: string | null): HrAuditQuery {
  const n = normalizeFilters(f);
  return {
    employee: n.employee || undefined, actor: n.actor || undefined, q: n.keyword || undefined,
    action: n.action || undefined, from: n.from || undefined, to: n.to || undefined,
    limit, cursor: cursor || undefined,
  };
}

/** "10/01/2026 – 10/14/2026 (Arizona time)" - MM/DD/YYYY through the shared HR date formatter. */
export function describeDateRange(f: Pick<ActivityFilters, 'from' | 'to'>): string | null {
  if (!f.from && !f.to) return null;
  const from = f.from ? formatLocalDate(f.from) : null;
  const to = f.to ? formatLocalDate(f.to) : null;
  const body = from && to ? `${from} – ${to}` : from ? `from ${from}` : `through ${to}`;
  return `${body} (Arizona time)`;
}

/** Honest fallback labels: never an invented person. */
export function actorLabel(ev: Pick<HrAuditEventRow, 'actorEmployeeId' | 'actorName'>): string {
  if (!ev.actorEmployeeId) return 'Unknown actor';
  if (ev.actorName && ev.actorName.trim()) return ev.actorName;
  return `Unresolved employee (${ev.actorEmployeeId.slice(0, 8)}…)`;
}
export function employeeLabel(ev: Pick<HrAuditEventRow, 'subjectEmployeeId' | 'subjectName'>): string | null {
  if (!ev.subjectEmployeeId) return null;
  if (ev.subjectName && ev.subjectName.trim()) return ev.subjectName;
  return `Unresolved employee (${ev.subjectEmployeeId.slice(0, 8)}…)`;
}

const ACCESS_TYPE_LABELS: Record<string, string> = {
  write: 'A change was recorded',
  read_sensitive: 'Sensitive information was viewed',
  download: 'A file was downloaded',
};
export function accessTypeLabel(accessType: string | null | undefined): string {
  if (!accessType) return '—';
  return ACCESS_TYPE_LABELS[accessType] || accessType;
}

export function shortRef(id: string | null | undefined): string {
  return id ? `${id.slice(0, 8)}…` : '—';
}

export function pageCount(total: number, limit: number): number {
  return Math.max(1, Math.ceil(total / Math.max(1, limit)));
}

/** "1–25 of 312" for the current page. */
export function describeRange(pageIndex: number, limit: number, shown: number, total: number): string {
  if (total === 0 || shown === 0) return '0';
  const first = pageIndex * limit + 1;
  return `${first}–${first + shown - 1} of ${total}`;
}

/** Paging is a stack of cursors: [null] is page 1; "Older" pushes the server's nextCursor,
 *  "Newer" pops. Any change to the filters or page size starts over at page 1. */
export function nextStack(stack: Array<string | null>, nextCursor: string | null): Array<string | null> {
  return nextCursor ? [...stack, nextCursor] : stack;
}
export function previousStack(stack: Array<string | null>): Array<string | null> {
  return stack.length > 1 ? stack.slice(0, -1) : stack;
}
